import { Formik, FormikHelpers } from "formik";
import React from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";

import LoggedInUserApi from "../../../api/LoggedInUserApi";
import { appConstants } from "../../../config/app-constants";
import { EToastvariant, IUserProfileForm } from "../../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux/Store";
import { fetchLoggedInUser, loggedInUserSelector } from "../../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import ScrollToFirstError from "../../ScrollToFirstError";
import PhoneInputField from "../../fields/PhoneInputField";
import { ButtonSpinner } from "../../placeholders";

const validationSchema = Yup.object({
	firstName: Yup.string()
		.trim()
		.min(3, "Please enter at least 3 characters")
		.max(255, "First Name must be less than 255 characters")
		.matches(appConstants.NAME_REGEX, "Invalid First Name")
		.required("First Name is required"),
	middleName: Yup.string()
		.trim()
		.min(3, "Please enter at least 3 characters")
		.max(255, "Middle Name must be less than 255 characters")
		.matches(appConstants.NAME_REGEX, "Invalid Middle Name")
		.notRequired(),
	lastName: Yup.string()
		.trim()
		.min(3, "Please enter at least 3 characters")
		.max(255, "Last Name must be less than 255 characters")
		.matches(appConstants.NAME_REGEX, "Invalid Last Name")
		.required("Last Name is required"),
	phoneNumber: Yup.string()
		.trim()
		.min(13, "Please enter valid phone number") // including country code +91
		.max(13, "Please enter valid phone number") // including country code +91
		.required("Phone number is required")
});

const EditProfile: React.FC = () => {
	const dispatch = useAppDispatch();

	const { loggedInUser } = useAppSelector(loggedInUserSelector);

	const initialValues: IUserProfileForm = {
		firstName: loggedInUser?.firstName || "",
		middleName: loggedInUser?.middleName || "",
		lastName: loggedInUser?.lastName || "",
		phoneNumber: loggedInUser?.phoneNumber || "",
		email: loggedInUser?.email || ""
	};

	const handleProfileUpdateFormSubmit = async (
		values: IUserProfileForm,
		{ setSubmitting }: FormikHelpers<IUserProfileForm>
	): Promise<void> => {
		try {
			setSubmitting(true);
			const _values = {
				firstName: values?.firstName || "",
				middleName: values?.middleName || "",
				lastName: values?.lastName || "",
				phoneNumber: values?.phoneNumber || ""
			};
			const userApi = LoggedInUserApi.getInstance();
			await userApi.updateMyProfile(_values);
			dispatch(fetchLoggedInUser());
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Updated",
					message: "Your profile updated successfully",
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Card className="form-card">
				<Card.Body>
					<Formik
						enableReinitialize={true}
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={handleProfileUpdateFormSubmit}
					>
						{({
							handleChange,
							handleBlur,
							handleSubmit,
							validateForm,
							values,
							errors,
							touched,
							isValid,
							submitCount,
							isSubmitting,
							setFieldValue
						}) => (
							<Form
								onSubmit={async (event) => {
									event.preventDefault();
									await validateForm();
									handleSubmit();
								}}
							>
								<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
								<Row className="align-items-center">
									<Col sm={3}>
										<Form.Label className="required-field mb-0">Name</Form.Label>
									</Col>
									<Col>
										<Row className="align-items-center">
											<Col sm={4}>
												<Form.Group className="form-group" controlId="firstName">
													<Form.Label className="small-label">First Name</Form.Label>
													<Form.Control
														type="text"
														autoComplete="false"
														placeholder="First Name"
														maxLength={255}
														name="firstName"
														value={values.firstName}
														onBlur={handleBlur("firstName")}
														onChange={handleChange("firstName")}
														isInvalid={Boolean(errors.firstName) && touched.firstName}
													/>
													<Form.Control.Feedback type="invalid">
														{errors.firstName}
													</Form.Control.Feedback>
												</Form.Group>
											</Col>
											<Col sm={4}>
												<Form.Group className="form-group" controlId="middleName">
													<Form.Label className="small-label">Middle Name</Form.Label>
													<Form.Control
														type="text"
														autoComplete="false"
														placeholder="Middle Name"
														maxLength={255}
														name="middleName"
														value={values.middleName}
														onBlur={handleBlur("middleName")}
														onChange={handleChange("middleName")}
														isInvalid={Boolean(errors.middleName) && touched.middleName}
													/>
													<Form.Control.Feedback type="invalid">
														{errors.middleName}
													</Form.Control.Feedback>
												</Form.Group>
											</Col>
											<Col sm={4}>
												<Form.Group className="form-group" controlId="lastName">
													<Form.Label className="small-label">Last Name</Form.Label>
													<Form.Control
														type="text"
														autoComplete="false"
														placeholder="Last Name"
														maxLength={255}
														name="lastName"
														value={values.lastName}
														onBlur={handleBlur("lastName")}
														onChange={handleChange("lastName")}
														isInvalid={Boolean(errors.lastName) && touched.lastName}
													/>
													<Form.Control.Feedback type="invalid">
														{errors.lastName}
													</Form.Control.Feedback>
												</Form.Group>
											</Col>
										</Row>
									</Col>
								</Row>
								<Row className="align-items-center">
									<Col sm={3}>
										<Form.Label className="required-field inline-label">Email</Form.Label>
									</Col>
									<Col>
										<Form.Group className="form-group" controlId="email">
											<Form.Control
												type="text"
												autoComplete="false"
												placeholder="Email"
												maxLength={255}
												name="email"
												value={values.email}
												onBlur={handleBlur("email")}
												onChange={handleChange}
												isInvalid={Boolean(errors.email) && touched.email}
												readOnly={true}
											/>
											<Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
										</Form.Group>
									</Col>
								</Row>
								<Row className="align-items-center">
									<Col sm={3}>
										<Form.Label className="required-field inline-label">Phone</Form.Label>
									</Col>
									<Col>
										<PhoneInputField
											name="phoneNumber"
											value={values.phoneNumber}
											onChange={(value) => {
												setFieldValue("phoneNumber", value);
											}}
											onBlur={handleBlur("phoneNumber")}
											isInvalid={Boolean(errors.phoneNumber) && touched.phoneNumber}
											errorMsg={errors.phoneNumber}
											formType="GENERAL"
										/>
									</Col>
								</Row>

								<div className="form-btns-wrap">
									{/* <Button
                    size="lg"
                    variant="outline-greyDark"
                    className="btn-w-120"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button> */}
									<Button size="lg" variant="primary" className="btn-w-160" type="submit">
										{!isSubmitting ? "Update" : <ButtonSpinner />}
									</Button>
								</div>
							</Form>
						)}
					</Formik>
				</Card.Body>
			</Card>
		</>
	);
};

export default EditProfile;
